import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';
import { formatDistanceToNow } from 'date-fns';

export default function RecycleBinPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isSuperAdmin } = useAuthStore();
  const queryClient = useQueryClient();
  const [deleteConfirmPhoto, setDeleteConfirmPhoto] = useState<{ photoId: string; caption: string } | null>(null);

  // Redirect if not super-admin
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    if (!isSuperAdmin) {
      navigate('/feed', { replace: true });
      return;
    }
  }, [isSuperAdmin, isAuthenticated, navigate]);

  // Fetch deleted photos
  const { data: photosData, isLoading: photosLoading } = useQuery({
    queryKey: ['recycle-bin-photos'],
    queryFn: () => api.listDeletedPhotos(),
    enabled: isAuthenticated && isSuperAdmin,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 0, // Always fetch fresh data
  });

  // Restore photo mutation
  const restorePhotoMutation = useMutation({
    mutationFn: (photoId: string) => api.restorePhoto(photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recycle-bin-photos'] });
      queryClient.invalidateQueries({ queryKey: ['photos'] });
    },
  });

  // Hard delete photo mutation
  const hardDeletePhotoMutation = useMutation({
    mutationFn: (photoId: string) => api.hardDeletePhoto(photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recycle-bin-photos'] });
      setDeleteConfirmPhoto(null); // Close dialog on success
    },
  });

  const handleDeleteForever = (photoId: string) => {
    hardDeletePhotoMutation.mutate(photoId);
  };

  const photos = photosData?.photos || [];

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <span className="text-4xl">🗑️</span>
            Recycle Bin
          </h1>
          <p className="text-gray-600">
            Restore deleted photos and comments. Items remain here until permanently deleted.
          </p>
        </div>

        {/* Deleted Photos Section */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>📸</span>
            Deleted Photos
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({photos.length})
            </span>
          </h2>

          {photosLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : photos.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">✨</div>
              <p className="text-gray-500 text-lg">No deleted photos</p>
              <p className="text-gray-400 text-sm mt-2">
                Deleted photos will appear here and can be restored
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {photos.map((photo: any) => (
                <div
                  key={photo.photoId}
                  className="bg-gray-50 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-primary-300 transition-all"
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-square bg-gray-200">
                    {photo.thumbnailUrls?.medium ? (
                      <img
                        src={photo.thumbnailUrls.medium}
                        alt={photo.caption || 'Deleted photo'}
                        className="w-full h-full object-cover opacity-60"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-6xl">📷</span>
                      </div>
                    )}
                    <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-semibold">
                      DELETED
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <p className="text-sm text-gray-700 mb-2 line-clamp-2">
                      {photo.caption || 'No caption'}
                    </p>
                    
                    <div className="space-y-1 text-xs text-gray-500 mb-3">
                      {photo.s3Key && (
                        <div className="flex items-center gap-1">
                          <span>📄</span>
                          <span className="truncate" title={photo.s3Key}>{photo.s3Key.split('/').pop()}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <span>🗑️</span>
                        <span>Deleted {formatDistanceToNow(new Date(photo.deletedAt), { addSuffix: true })}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span>👤</span>
                        <span>By {photo.deleterName}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span>📅</span>
                        <span>Uploaded {formatDistanceToNow(new Date(photo.createdAt), { addSuffix: true })}</span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-2">
                      <button
                        onClick={() => restorePhotoMutation.mutate(photo.photoId)}
                        disabled={restorePhotoMutation.isPending || hardDeletePhotoMutation.isPending}
                        className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        {restorePhotoMutation.isPending ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Restoring...</span>
                          </>
                        ) : (
                          <>
                            <span>↩️</span>
                            <span>Restore Photo</span>
                          </>
                        )}
                      </button>
                      
                      <button
                        onClick={() => setDeleteConfirmPhoto({ photoId: photo.photoId, caption: photo.caption || 'No caption' })}
                        disabled={restorePhotoMutation.isPending || hardDeletePhotoMutation.isPending}
                        className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        {hardDeletePhotoMutation.isPending ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Deleting...</span>
                          </>
                        ) : (
                          <>
                            <span>🗑️</span>
                            <span>Delete Forever</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Deleted Comments Section (Placeholder) */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>💬</span>
            Deleted Comments
          </h2>
          <div className="text-center py-8">
            <p className="text-gray-500">
              Comment recycle bin coming soon
            </p>
            <p className="text-gray-400 text-sm mt-2">
              Requires database index setup for efficient querying
            </p>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirmPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            {/* Warning Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-4xl">⚠️</span>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-2">
              Permanently Delete Photo?
            </h3>

            {/* Message */}
            <div className="text-center mb-6">
              <p className="text-gray-600 mb-3">
                This action <span className="font-bold text-red-600">CANNOT be undone</span>.
              </p>
              <p className="text-sm text-gray-500 mb-2">
                The photo and all its data will be deleted forever:
              </p>
              <div className="bg-gray-50 rounded-lg p-3 text-left">
                <p className="text-sm text-gray-700 italic line-clamp-2">
                  "{deleteConfirmPhoto.caption}"
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirmPhoto(null)}
                disabled={hardDeletePhotoMutation.isPending}
                className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-gray-800 font-semibold rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteForever(deleteConfirmPhoto.photoId)}
                disabled={hardDeletePhotoMutation.isPending}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {hardDeletePhotoMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <span>🗑️</span>
                    <span>Delete Forever</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

